/**
 * Rates hooks using React Relay
 *
 * These hooks provide access to rate data through Relay's
 * normalized cache and automatic data management.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLazyLoadQuery, usePaginationFragment, fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useRatesRelayQuery as UseRatesRelayQuery,
  useRatesRelayQuery$variables,
  RateFilterInput,
  RateSortInput,
} from './__generated__/useRatesRelayQuery.graphql';
import type { useRatesRelayDirectQuery as UseRatesRelayDirectQuery } from './__generated__/useRatesRelayDirectQuery.graphql';
import type { useRatesRelayByIdQuery } from './__generated__/useRatesRelayByIdQuery.graphql';
import type { useRatesRelayPaginationFragment$key } from './__generated__/useRatesRelayPaginationFragment.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

const RatesListPaginationFragment = graphql`
  fragment useRatesRelayPaginationFragment on RootQueryType
  @refetchable(queryName: "useRatesRelayPaginationQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 100 }
    after: { type: "String" }
    filter: { type: "RateFilterInput" }
    sort: { type: "[RateSortInput!]" }
  ) {
    listRates(first: $first, after: $after, filter: $filter, sort: $sort)
      @connection(
        key: "useRatesRelayPaginationFragment_listRates"
        filters: ["filter", "sort"]
      ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          name
          activeOn
          archivedOn
          baseRate
          baseRateIncludedTime
          creditCardHold
          freeTime
          hidden
          maxPeriods
          maxRentals
          membershipRequired
          minimumWalletDeposit
          periodDuration
          pricePerPeriod
          systemId
          systemOldId
          organizationId
          createdOn
          system {
            id
            oldId
            name
          }
        }
      }
    }
  }
`;

const RatesListQuery = graphql`
  query useRatesRelayQuery(
    $first: Int
    $after: String
    $filter: RateFilterInput
    $sort: [RateSortInput!]
  ) {
    ...useRatesRelayPaginationFragment
      @arguments(first: $first, after: $after, filter: $filter, sort: $sort)
  }
`;

const RatesListDirectQuery = graphql`
  query useRatesRelayDirectQuery(
    $first: Int
    $after: String
    $filter: RateFilterInput
    $sort: [RateSortInput!]
  ) {
    listRates(first: $first, after: $after, filter: $filter, sort: $sort) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          name
          activeOn
          archivedOn
          baseRate
          baseRateIncludedTime
          creditCardHold
          freeTime
          hidden
          maxPeriods
          maxRentals
          membershipRequired
          minimumWalletDeposit
          periodDuration
          pricePerPeriod
          systemId
          systemOldId
          organizationId
          createdOn
          system {
            id
            oldId
            name
          }
        }
      }
    }
  }
`;

const RateByIdQuery = graphql`
  query useRatesRelayByIdQuery($id: ID!) {
    getRate(id: $id) {
      id
      oldId
      name
      activeOn
      archivedOn
      baseRate
      baseRateIncludedTime
      creditCardHold
      freeTime
      hidden
      maxPeriods
      maxRentals
      membershipRequired
      minimumWalletDeposit
      periodDuration
      pricePerPeriod
      systemId
      systemOldId
      organizationId
      createdOn
      system {
        id
        oldId
        name
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { RateFilterInput, RateSortInput };

export interface RateSystemGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
}

export interface RateGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
  activeOn?: string | null;
  archivedOn?: string | null;
  baseRate?: number | null;
  baseRateIncludedTime?: number | null;
  creditCardHold?: number | null;
  freeTime?: number | null;
  hidden?: boolean | null;
  maxPeriods?: number | null;
  maxRentals?: number | null;
  membershipRequired: boolean;
  minimumWalletDeposit?: number | null;
  periodDuration?: number | null;
  pricePerPeriod?: number | null;
  systemId?: string | null;
  systemOldId: string;
  organizationId?: string | null;
  createdOn: string;
  system?: RateSystemGraphQL | null;
}

export interface RateQueryFilter {
  systemId?: string;
  systemOldId?: string;
  organizationId?: string;
  hidden?: boolean;
  name?: string;
  archivedOn?: { isNil?: boolean };
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Suspense-based hook for fetching rates list
 */
export function useRatesRelay(
  filter?: RateQueryFilter,
  options?: {
    limit?: number;
    sort?: RateSortInput[];
  }
) {
  const limit = options?.limit ?? 100;
  const sort = options?.sort ?? [{ field: 'NAME', order: 'ASC' }];

  const variables: useRatesRelayQuery$variables = {
    first: limit,
    filter: buildRelayFilter(filter),
    sort,
  };

  const queryData = useLazyLoadQuery<UseRatesRelayQuery>(RatesListQuery, variables);

  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseRatesRelayQuery,
    useRatesRelayPaginationFragment$key
  >(RatesListPaginationFragment, queryData);

  const nodes = data.listRates?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as RateGraphQL[],
    count: data.listRates?.count,
    pageInfo: data.listRates?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

/**
 * Suspense-based hook for fetching a single rate by ID
 */
export function useRateByIdRelay(rateId: string) {
  const data = useLazyLoadQuery<useRatesRelayByIdQuery>(RateByIdQuery, {
    id: rateId,
  });

  return data.getRate as RateGraphQL | null;
}

// ============================================================================
// React Query-like Interface (No Suspense)
// ============================================================================

/**
 * Hook that provides React Query-like interface for rates data
 */
export function useRatesRelayQuery(
  filter?: RateQueryFilter,
  options?: {
    limit?: number;
    sort?: RateSortInput[];
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<RateGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const limit = options?.limit ?? 100;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'NAME' as const, order: 'ASC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;

  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const variables: useRatesRelayQuery$variables = {
          first: limit,
          filter: buildRelayFilter(filter),
          sort,
        };

        const result = await fetchQuery<UseRatesRelayDirectQuery>(
          relayEnvironment,
          RatesListDirectQuery,
          variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listRates?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];
          setData(nodes as RateGraphQL[]);
          setCount(result.listRates?.count ?? undefined);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch rates'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

/**
 * Hook for fetching a single rate by ID with explicit loading state
 */
export function useRateByIdRelayQuery(
  rateId: string | undefined,
  options?: { enabled?: boolean }
) {
  const [data, setData] = useState<RateGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(rateId);

  useEffect(() => {
    if (!enabled || !rateId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

        const result = await fetchQuery<useRatesRelayByIdQuery>(
          relayEnvironment,
          RateByIdQuery,
          { id: rateId }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          setData(result.getRate as RateGraphQL | null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch rate'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, rateId, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return { data, isLoading, isFetched, error, refetch };
}

// ============================================================================
// Helpers
// ============================================================================

export function buildRelayFilter(filter?: RateQueryFilter): RateFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: RateFilterInput = {};

  if (filter.systemId) {
    relayFilter.systemId = { eq: filter.systemId };
  }
  if (filter.systemOldId) {
    relayFilter.systemOldId = { eq: filter.systemOldId };
  }
  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }
  if (filter.hidden !== undefined) {
    relayFilter.hidden = { eq: filter.hidden };
  }
  if (filter.name) {
    if (filter.name.includes('%')) {
      relayFilter.name = { ilike: filter.name };
    } else {
      relayFilter.name = { ilike: `%${filter.name}%` };
    }
  }
  if (filter.archivedOn) {
    relayFilter.archivedOn = filter.archivedOn;
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}
